import {
  ContractCancelTypeEnum,
  ContractEndActionEnum,
  ContractStatusEnum,
  ICancelContractUISchema,
  IContract,
  getContractUIRequestSchema,
} from '@monetize/types/app';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { toDateOnly } from '@monetize/utils/core';
import { clamp } from 'date-fns/clamp';
import { formatISO } from 'date-fns/formatISO';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { handleApiErrorToast } from '../../../api/axios';
import {
  doAmendContract,
  doRenewContract,
  useCancelContract,
  useUpdateContractById,
} from '../../../api/cpqService';
import DataTableActions, {
  ActionType,
} from '../../../components/Monetize/DataTable/MDataTableActions';
import { getQuoteEditRoute } from '../../../constants/routes';
import { useExperienceConfiguration } from '../../../hooks';
import { IContractWithBillGroup } from '../../../hooks/AccountDetail';
import { useACL } from '../../../services/acl/acl';
import { useAuth } from '../../../services/auth0';
import { useConfirmModal } from '../../../services/confirmModal';
import { logger } from '../../../services/logger';
import { CONTRACT_CANCELED_RENEWAL_MESSAGE } from '../../../constants/contracts';
import {
  canCancelContract,
  canTurnOffAutoRenew,
  getAmendContractDisabledReason,
  getRenewContractDisabledReason,
} from '../../../utils/contracts';
import { CancelContractForm } from './components/CancelContractForm';

type ContractActionsProps = {
  contract: IContract;
  handleEarlyRenewal?: (contract: IContractWithBillGroup) => void;
};

/**
 * Amend / renew / cancel actions for a contract, including the confirm flows
 * they open. Shared by the row kebab and the contract modal's action buttons.
 */
export function useContractActions({
  contract,
  handleEarlyRenewal,
}: ContractActionsProps) {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const { canDo } = useACL();
  const {
    showConfirmModal: showCancelContractConfirmModal,
    closeModal: closeCancelContractConfirmModal,
    setModalLoading: setCancelContractModalLoading,
  } = useConfirmModal();
  const updateContract = useUpdateContractById();
  const {
    showConfirmModal: showAutoTurnOffConfirmModal,
    closeModal: closeAutoTurnOffConfirmModal,
    setModalLoading: setAutoTurnOffConfirmModalLoading,
  } = useConfirmModal();
  const { enableEarlyRenewal } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );

  const canCreateContracts = canDo([['contracts', 'create']]);

  const { mutateAsync: doCancelContract } = useCancelContract();

  const defaultValues = {
    type: ContractCancelTypeEnum.SPECIFIC_DATE,
    date: clamp(formatISO(new Date()), {
      start: contract.startDate,
      end: contract.endDate,
    }).toISOString(),
  };

  const { control, clearErrors, handleSubmit } =
    useForm<ICancelContractUISchema>({
      resolver: getContractUIRequestSchema(contract),
      mode: 'onChange',
      defaultValues,
    });

  const onCancelContractSubmit = async ({
    type,
    date,
  }: ICancelContractUISchema) => {
    try {
      setCancelContractModalLoading(true);
      const amendmentEffectiveDate = toDateOnly(
        type === ContractCancelTypeEnum.ENTIRE_CONTRACT
          ? contract.startDate
          : date || new Date(),
        'userTimezone',
      );
      const quote = await doCancelContract({
        contractId: contract.id,
        amendmentEffectiveDate,
        ownerId: userId,
      });
      if (quote.id) {
        closeCancelContractConfirmModal();
        navigate(getQuoteEditRoute(quote.id));
      }
    } catch (error) {
      handleApiErrorToast(error);
      setCancelContractModalLoading(false);
    }
  };

  const onCancelContractError = (error: any) => {
    logger.error('Cancel Contract Error ====>', error);
  };

  const handleAmendContract = async (contractId: string) => {
    try {
      const amendedQuote = await doAmendContract(contractId);
      amendedQuote?.id &&
        navigate({
          pathname: getQuoteEditRoute(amendedQuote.id),
        });
    } catch (error) {
      handleApiErrorToast(error);
    }
  };

  const handleRenewContract = async (contractId: string) => {
    if (
      enableEarlyRenewal &&
      handleEarlyRenewal &&
      contract.status !== ContractStatusEnum.FINISHED
    ) {
      handleEarlyRenewal(contract);
      return;
    }

    try {
      const renewedQuote = await doRenewContract(contractId);
      renewedQuote?.id &&
        navigate({
          pathname: getQuoteEditRoute(renewedQuote.id),
        });
    } catch (error) {
      handleApiErrorToast(error);
    }
  };

  const handleTurnOffAutoRenew = async (contractId: string) => {
    showAutoTurnOffConfirmModal({
      title: 'Turn off Auto Renew',
      description:
        'This contract is set to Auto Renew. By turning off Auto Renew, the Contract must be renewed manually. Once turned off, to turn Auto Renew back on, an Amendment Quote is required.',
      onYes: async () => {
        try {
          setAutoTurnOffConfirmModalLoading(true);
          await updateContract.mutateAsync({
            id: contractId,
            payload: { endAction: ContractEndActionEnum.CANCEL },
          });
        } catch (error) {
          handleApiErrorToast(error);
        } finally {
          setAutoTurnOffConfirmModalLoading(false);
          closeAutoTurnOffConfirmModal();
        }
      },
      yesButton: 'Turn Off',
      yesBtnProps: {
        variant: 'delete',
      },
      noBtnProps: {
        variant: 'cancel',
      },
    });
  };

  const handleCancelContract = () => {
    showCancelContractConfirmModal({
      title: 'Cancel Contract',
      content: (
        <CancelContractForm
          contract={contract}
          control={control}
          clearErrors={clearErrors}
        />
      ),
      onYes: handleSubmit(onCancelContractSubmit, onCancelContractError),
      onNo: closeCancelContractConfirmModal,
      yesButton: 'Next',
      yesBtnProps: {
        variant: 'primary',
      },
      noBtnProps: {
        variant: 'cancel',
      },
    });
  };

  // Only set when the action is unavailable, so it never shows on an enabled item
  const amendDisabledReason = canCreateContracts
    ? getAmendContractDisabledReason(contract)
    : 'You do not have permission to amend contracts.';
  const renewDisabledReason = canCreateContracts
    ? getRenewContractDisabledReason(contract)
    : 'You do not have permission to renew contracts.';

  return {
    amend: {
      title: 'Amend',
      enabled: !amendDisabledReason,
      tooltipMsg: amendDisabledReason ?? undefined,
      action: () => contract?.id && handleAmendContract(contract.id),
    },
    renew: {
      title: 'Renew',
      enabled: !renewDisabledReason,
      tooltipMsg: renewDisabledReason ?? undefined,
      action: () => contract?.id && handleRenewContract?.(contract.id),
    },
    renewCanceled: {
      title: 'Renew',
      enabled:
        canCreateContracts && contract.status === ContractStatusEnum.CANCELED,
      isDisabled: true,
      tooltipMsg: CONTRACT_CANCELED_RENEWAL_MESSAGE,
      action: () => {},
    },
    turnOffAutoRenew: {
      title: 'Turn off Auto Renew',
      color: 'danger',
      enabled: canCreateContracts && canTurnOffAutoRenew(contract),
      action: () => contract?.id && handleTurnOffAutoRenew(contract.id),
    },
    cancel: {
      title: 'Cancel',
      enabled: canCreateContracts && canCancelContract(contract),
      color: 'danger',
      action: handleCancelContract,
    },
  } satisfies Record<string, ActionType>;
}

const ContractActions: FC<ContractActionsProps> = ({
  contract,
  handleEarlyRenewal,
}: ContractActionsProps) => {
  const { amend, renew, renewCanceled, turnOffAutoRenew, cancel } =
    useContractActions({ contract, handleEarlyRenewal });
  return (
    <DataTableActions
      actions={[amend, renew, renewCanceled, turnOffAutoRenew, cancel]}
      variant="action"
    />
  );
};

export default ContractActions;
